import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import AdminSidebar from "@/components/AdminSidebar";
import { useNotificationCount } from "@/hooks/useNotificationCount";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Menu, Printer, FileText, MapPin, Calendar, Scissors, ChevronLeft, ChevronRight, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CutReport = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState([]);
  const [month, setMonth] = useState(() => format(new Date(), "yyyy-MM"));
  const [monthly, setMonthly] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    fetchReport();
  }, []);

  useEffect(() => {
    fetchMonthly();
  }, [month]);

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/admin/cut-report`, { withCredentials: true });
      setReportData(res.data);
    } catch (error) {
      console.error("Cut report error:", error?.response?.status, error?.response?.data);
      const status = error?.response?.status;
      if (status === 401) {
        toast.error("Session expired — please log in again");
      } else if (status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load cut report");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthly = async () => {
    setMonthlyLoading(true);
    try {
      const res = await axios.get(`${API}/admin/cut-report/monthly`, {
        params: { month },
        withCredentials: true,
      });
      setMonthly(res.data);
    } catch (error) {
      console.error("Monthly cuts error:", error?.response?.status, error?.response?.data);
      toast.error("Failed to load monthly cuts");
      setMonthly({ month, count: 0, total_amount: 0, paid_count: 0, unpaid_count: 0, cuts: [] });
    } finally {
      setMonthlyLoading(false);
    }
  };

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(format(d, "yyyy-MM"));
  };

  const monthLabel = (() => {
    try { return format(parseISO(`${month}-01`), "MMMM yyyy"); } catch { return month; }
  })();

  const handlePrint = () => {
    const content = printRef.current;
    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>Zig Zag Lawncare - Cut Report</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 24px; color: #222; }
            h1 { font-size: 20px; margin-bottom: 4px; }
            .subtitle { color: #666; font-size: 12px; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 13px; }
            th { background: #f0f0f0; text-align: left; padding: 8px 12px; border-bottom: 2px solid #ccc; font-weight: bold; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; }
            td { padding: 8px 12px; border-bottom: 1px solid #e0e0e0; }
            tr:nth-child(even) { background: #fafafa; }
            .never { color: #999; font-style: italic; }
            .footer { margin-top: 20px; font-size: 11px; color: #999; text-align: center; }
          </style>
        </head>
        <body>
          <h1>Zig Zag Lawncare Okinawa - Cut Report</h1>
          <p class="subtitle">Generated ${format(new Date(), "MMMM d, yyyy 'at' h:mm a")} &bull; ${reportData.length} customers</p>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Address</th>
                <th>Last Cut Date</th>
                <th>Days Since</th>
              </tr>
            </thead>
            <tbody>
              ${reportData.map((c, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${c.name}${c.email ? '<br><span style="color:#999;font-size:11px">' + c.email + '</span>' : ''}</td>
                  <td>${c.address || '<span class="never">No address</span>'}</td>
                  <td>${c.last_cut_date ? format(parseISO(c.last_cut_date), "MMM d, yyyy") : '<span class="never">Never</span>'}</td>
                  <td>${c.days_since !== null ? c.days_since + ' days' : '-'}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
          <p class="footer">Zig Zag Lawncare Okinawa &bull; Professional Lawn Care</p>
        </body>
      </html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} notificationCount={notificationCount} />

      <main className="flex-1 p-4 lg:p-8 overflow-x-auto">
        {/* Mobile Header */}
        <div className="flex items-center gap-4 mb-6 lg:hidden">
          <button onClick={() => setSidebarOpen(true)} className="text-white">
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="font-heading text-2xl font-bold text-white uppercase">Cut Report</h1>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="hidden lg:block font-heading text-3xl font-bold text-white uppercase">Cut Report</h1>
            <p className="text-[#A1A1AA] text-sm mt-1">{reportData.length} customers &bull; Last cut dates</p>
          </div>
          <Button
            onClick={handlePrint}
            className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
            data-testid="print-report-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print Report
          </Button>
        </div>

        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="bg-[#121212] border border-[#27272A] mb-4">
            <TabsTrigger value="summary" data-testid="tab-summary" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
              <FileText className="w-4 h-4 mr-2" /> Last Cut Summary
            </TabsTrigger>
            <TabsTrigger value="monthly" data-testid="tab-monthly" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
              <Calendar className="w-4 h-4 mr-2" /> Monthly Cuts Completed
            </TabsTrigger>
          </TabsList>

          {/* MONTHLY TAB */}
          <TabsContent value="monthly">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Button data-testid="month-prev-btn" variant="outline" size="icon"
                  className="border-[#27272A] text-white hover:bg-[#27272A]" onClick={() => shiftMonth(-1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <input
                  data-testid="month-picker"
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="bg-[#121212] border border-[#27272A] text-white rounded-md px-3 py-2 text-sm"
                />
                <Button data-testid="month-next-btn" variant="outline" size="icon"
                  className="border-[#27272A] text-white hover:bg-[#27272A]" onClick={() => shiftMonth(1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              {monthly && (
                <div className="flex items-center gap-3 text-sm">
                  <span data-testid="monthly-count" className="bg-[#39FF14]/10 text-[#39FF14] px-3 py-1.5 rounded-full font-semibold">
                    {monthly.count} cuts
                  </span>
                  <span data-testid="monthly-total" className="bg-[#FACC15]/10 text-[#FACC15] px-3 py-1.5 rounded-full font-semibold flex items-center gap-1">
                    <DollarSign className="w-3.5 h-3.5" />{(monthly.total_amount || 0).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
              {monthlyLoading ? (
                <div className="text-center py-16">
                  <div className="w-8 h-8 border-2 border-[#39FF14] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">Loading {monthLabel}...</p>
                </div>
              ) : !monthly || monthly.cuts.length === 0 ? (
                <div className="text-center py-16">
                  <Scissors className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">No cuts completed in {monthLabel}</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272A]">
                      <th className="text-left px-5 py-3 text-[#A1A1AA] text-xs font-bold uppercase tracking-wider">#</th>
                      <th className="text-left px-5 py-3 text-[#A1A1AA] text-xs font-bold uppercase tracking-wider">Customer</th>
                      <th className="text-left px-5 py-3 text-[#A1A1AA] text-xs font-bold uppercase tracking-wider">Address</th>
                      <th className="text-left px-5 py-3 text-[#A1A1AA] text-xs font-bold uppercase tracking-wider">Date</th>
                      <th className="text-left px-5 py-3 text-[#A1A1AA] text-xs font-bold uppercase tracking-wider">Amount</th>
                      <th className="text-left px-5 py-3 text-[#A1A1AA] text-xs font-bold uppercase tracking-wider">Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthly.cuts.map((cut, idx) => (
                      <tr key={`${cut.customer_id}-${idx}`} data-testid={`monthly-cut-row-${idx}`}
                        className="border-b border-[#27272A]/50 hover:bg-[#39FF14]/5 transition-colors">
                        <td className="px-5 py-3 text-[#52525B] text-sm">{idx + 1}</td>
                        <td className="px-5 py-3 text-white text-sm font-medium">{cut.customer_name}</td>
                        <td className="px-5 py-3">
                          {cut.address ? (
                            <div className="flex items-start gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-[#39FF14] mt-0.5 shrink-0" />
                              <span className="text-[#A1A1AA] text-sm">{cut.address}</span>
                            </div>
                          ) : <span className="text-[#52525B] text-sm italic">No address</span>}
                        </td>
                        <td className="px-5 py-3 text-white text-sm">{format(parseISO(cut.date), "MMM d, yyyy")}</td>
                        <td className="px-5 py-3 text-[#39FF14] text-sm font-semibold">${(cut.amount || 0).toFixed(2)}</td>
                        <td className="px-5 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            cut.payment_status === "paid" ? "bg-[#39FF14]/10 text-[#39FF14]" : "bg-red-500/10 text-red-400"
                          }`}>
                            {cut.payment_status === "paid" ? "Paid" : "Unpaid"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* SUMMARY TAB */}
          <TabsContent value="summary">
        {/* Report Table */}
        <div ref={printRef} className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
          {loading ? (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-[#39FF14] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[#A1A1AA]">Loading report...</p>
            </div>
          ) : reportData.length === 0 ? (
            <div className="text-center py-16">
              <Scissors className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">No customers with completed cuts</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#27272A]">
                  <th className="text-left px-5 py-3 text-[#A1A1AA] text-xs font-bold uppercase tracking-wider">#</th>
                  <th className="text-left px-5 py-3 text-[#A1A1AA] text-xs font-bold uppercase tracking-wider">Customer</th>
                  <th className="text-left px-5 py-3 text-[#A1A1AA] text-xs font-bold uppercase tracking-wider">Address</th>
                  <th className="text-left px-5 py-3 text-[#A1A1AA] text-xs font-bold uppercase tracking-wider">Last Cut</th>
                  <th className="text-left px-5 py-3 text-[#A1A1AA] text-xs font-bold uppercase tracking-wider">Days Since</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((customer, idx) => (
                  <tr
                    key={customer.customer_id}
                    data-testid={`report-row-${customer.customer_id}`}
                    className={`border-b border-[#27272A]/50 hover:bg-[#39FF14]/5 transition-colors ${
                      customer.days_since !== null && customer.days_since > 30 ? "bg-red-500/5" : ""
                    }`}
                  >
                    <td className="px-5 py-3 text-[#52525B] text-sm">{idx + 1}</td>
                    <td className="px-5 py-3">
                      <p className="text-white text-sm font-medium">{customer.name}</p>
                      {customer.email && <p className="text-[#52525B] text-xs">{customer.email}</p>}
                      {customer.phone && <p className="text-[#52525B] text-xs">{customer.phone}</p>}
                    </td>
                    <td className="px-5 py-3">
                      {customer.address ? (
                        <div className="flex items-start gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-[#39FF14] mt-0.5 shrink-0" />
                          <span className="text-[#A1A1AA] text-sm">{customer.address}</span>
                        </div>
                      ) : (
                        <span className="text-[#52525B] text-sm italic">No address</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {customer.last_cut_date ? (
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-[#39FF14] shrink-0" />
                          <span className="text-white text-sm">{format(parseISO(customer.last_cut_date), "MMM d, yyyy")}</span>
                        </div>
                      ) : (
                        <span className="text-[#52525B] text-sm italic">Never</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {customer.days_since !== null ? (
                        <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${
                          customer.days_since <= 14 ? "bg-[#39FF14]/10 text-[#39FF14]" :
                          customer.days_since <= 30 ? "bg-yellow-400/10 text-yellow-400" :
                          "bg-red-500/10 text-red-400"
                        }`}>
                          {customer.days_since}d ago
                        </span>
                      ) : (
                        <span className="text-[#52525B] text-sm">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default CutReport;
